/**
 * TokenCard — shared list-row component used by Home (myTokens + recent
 * launches feeds). Renders an avatar, ticker, "X / Y TON" label, and a
 * progress bar showing fill-toward-graduation.
 *
 * CC-025 (Home feed bug fix)
 * ──────────────────────────
 *   The previous inline `TokenRow` in `Home.tsx` had two bugs:
 *
 *     1. `Number(BigInt(realTon) / 1_000_000_000n)` did BigInt division
 *        first, truncating 99_000_000n (0.099 TON) to 0n. Every sub-TON
 *        curve looked empty.
 *     2. `const targetTon = 1500` was hardcoded. Even after the
 *        progress-bar fix, the label said "0 / 1500 TON" on a 1-TON-
 *        target dev curve.
 *
 *   The fix routes both surfaces (myTokens + recent feed) through this
 *   shared component, which sources `targetTon` from the API and runs
 *   the conversion via `lib/displayMath`.
 *
 * Not used by the Token detail header — that has a richer layout
 * (bigger image, BUY/SELL panel directly below). Its progress math
 * also uses `displayMath` for consistency.
 */

import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import type { DexStatus } from '@memepad/shared';
import { nanoToTonDisplay, progressPct } from '../lib/displayMath';
import { stonfiSwapUrl, okxTokenUrl } from '../lib/dex';
import { DexBadge } from './DexBadge';

// CC-046 — open an external DEX page via the Mini App SDK. Mirrors the
// helper in Token.tsx; kept local so this change stays confined to
// TokenCard.tsx (a future cleanup could DRY both into lib/dex.ts).
function openDexLink(url: string): void {
  const tg = window.Telegram?.WebApp;
  if (tg && typeof tg.openLink === 'function') {
    tg.openLink(url);
  } else {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

// CC-046 followup — compact "chip" button for the in-card DEX links.
// A scaled-down take on Token.tsx's primary graduated buttons: accent
// border + semi-transparent accent fill so it clearly reads as tappable
// (the plain `secondary` grey was nearly invisible).
const CHIP_STYLE: CSSProperties = {
  padding: '6px 12px',
  fontSize: 13,
  fontWeight: 600,
  borderRadius: 12,
  border: '1px solid var(--button)',
  background: 'rgba(82, 136, 193, 0.16)',
  color: 'var(--button)',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  lineHeight: 1.2,
};

export interface TokenCardProps {
  ticker: string;
  masterAddr: string;
  imageCid: string;
  /** Curve reserves in nano-TON (bigint or stringified BigInt from JSON). */
  realTonReserves: bigint | string;
  /** Per-token graduation target in nano-TON. NOTE: sourced from the
   *  API, never hardcoded. */
  targetTon: bigint | string;
  isGraduated: boolean;
  /** CC-027: dex/migration lifecycle — drives the migration badge.
   *  null on legacy rows → no badge. */
  dexStatus: DexStatus | null;
}

export function TokenCard({
  ticker,
  masterAddr,
  imageCid,
  realTonReserves,
  targetTon,
  isGraduated,
  dexStatus,
}: TokenCardProps) {
  // Coerce defensively — JSON round-trips bigint as string.
  const realNano = BigInt(realTonReserves);
  const targetNano = BigInt(targetTon);
  // CC-027 followup: nanoToTonDisplay now returns a trimmed string
  // ("0", "0.099", "12.5") — no per-call toFixed/decimals heuristic.
  const realTonDisplay = nanoToTonDisplay(realNano);
  const targetTonDisplay = nanoToTonDisplay(targetNano);
  const pct = progressPct(realNano, targetNano);

  // CC-027 followup: a token that failed to migrate shows only its
  // header (icon + ticker + ❌ badge) — no reserves label, no progress.
  const showCurveInfo = dexStatus !== 'migration_failed' && !isGraduated;

  return (
    <Link to={`/token/${masterAddr}`} className="card row" style={{ display: 'flex' }}>
      <img
        className="token-img"
        src={`https://gateway.pinata.cloud/ipfs/${imageCid}`}
        alt={ticker}
        loading="lazy"
      />
      <div style={{ flex: 1 }}>
        <div className="between">
          {/* Left zone: ticker (+ migration badge for non-graduated). */}
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            <strong>${ticker}</strong>
            {dexStatus !== 'graduated' && (
              <DexBadge
                dexStatus={dexStatus}
                isGraduated={isGraduated}
                masterAddr={masterAddr}
              />
            )}
          </span>
          {/* CC-046: a graduated token's right zone holds STON.fi / OKX
              chip buttons (replacing the old clickable ✅ badge). Both
              open the DEX directly; stopPropagation keeps the card's
              <Link> from navigating to the detail page on a chip tap. */}
          {dexStatus === 'graduated' && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
              <button
                style={CHIP_STYLE}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  openDexLink(stonfiSwapUrl(masterAddr));
                }}
              >
                STON.fi
              </button>
              <button
                style={CHIP_STYLE}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  openDexLink(okxTokenUrl(masterAddr));
                }}
              >
                OKX
              </button>
            </span>
          )}
          {/* CC-027 followup: migration_failed cards drop the reserves
              label entirely; graduated cards show the chips above instead. */}
          {showCurveInfo && (
            <span className="muted">
              {realTonDisplay} / {targetTonDisplay} TON
            </span>
          )}
        </div>
        {showCurveInfo && (
          <div className="progress" style={{ marginTop: 8 }}>
            <div className="progress-bar" style={{ width: `${pct}%` }} />
          </div>
        )}
      </div>
    </Link>
  );
}
